import { useGetOne } from "ra-core";
import { Flame, User, Building2 } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { EmbeddedAgentView } from "./EmbeddedAgentView";

// Sprint 10.x — Fenêtre de discussion agent ouverte depuis les briefs
// IA (contact / company). Préfetche les infos clés + injecte un résumé
// markdown dans le contexte LLM pour que l'agent ait la fiche "en main"
// dès la première question.

interface BriefAgentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "contact" | "company";
  entityId: number | string;
  entityLabel: string;
  briefContent?: string | null;
}

const SUGGESTED_PROMPTS_CONTACT = [
  "Quel ton adopter pour relancer cette personne ?",
  "Quelles questions poser au prochain appel ?",
  "Historique récent et signaux importants ?",
  "Comment qualifier ce lead en 3 lignes ?",
];

const SUGGESTED_PROMPTS_COMPANY = [
  "Donne-moi un état des lieux de cette entreprise",
  "Quels contacts prioriser pour avancer le deal ?",
  "Signaux d'achat récents sur ce compte ?",
  "Stratégie de relance recommandée",
];

type ContactSummaryRow = {
  id: number;
  first_name?: string;
  last_name?: string;
  email_fts?: string;
  phone_fts?: string;
  company_name?: string;
  temperature?: string | null;
  next_action_type?: string | null;
  next_action_date?: string | null;
  heat_score?: number | null;
  heat_tier?: string | null;
  heat_reason?: string | null;
  heat_updated_at?: string | null;
  population?: string | null;
  source?: string | null;
  nb_tasks?: number;
  nb_unread_emails?: number;
};

type CompanyOverviewRow = {
  id: number;
  name?: string;
  sector?: string;
  size?: string | number;
  city?: string;
  contacts_total?: number;
  contacts_actifs?: number;
  avg_heat_score?: number | null;
  max_heat_tier?: string | null;
  max_temperature?: string | null;
  payment_state?: string;
  is_paying_loose?: boolean;
  has_overdue_tasks?: boolean;
  overdue_tasks_count?: number;
  nb_open_deals?: number;
  nb_active_plans?: number;
  pipeline_open_eur?: number;
  mrr_monthly_eur?: number;
  age_days?: number;
};

function fmtContactSummary(c: ContactSummaryRow, briefContent?: string | null) {
  const name =
    `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || `#${c.id}`;
  const lines: string[] = [];
  lines.push(
    `**Contact :** ${name}${c.company_name ? ` — ${c.company_name}` : ""}`,
  );
  const emails = (c.email_fts ?? "").replace(/[[\]"]/g, "").trim();
  if (emails) lines.push(`Emails : ${emails}`);
  const phones = (c.phone_fts ?? "").replace(/[[\]"]/g, "").trim();
  if (phones) lines.push(`Téléphones : ${phones}`);
  if (c.population || c.source)
    lines.push(
      `Population/Source : ${c.population ?? "—"} / ${c.source ?? "—"}`,
    );
  if (c.temperature) lines.push(`Température (manuelle) : ${c.temperature}`);
  if (c.heat_score !== null && c.heat_score !== undefined)
    lines.push(
      `Heat score IA : ${c.heat_score}/100${c.heat_tier ? ` (${c.heat_tier})` : ""}${c.heat_reason ? ` — ${c.heat_reason}` : ""}`,
    );
  if (c.next_action_type && c.next_action_type !== "aucune")
    lines.push(
      `Prochaine action : ${c.next_action_type}${c.next_action_date ? ` le ${c.next_action_date}` : ""}`,
    );
  if (c.nb_tasks) lines.push(`Tasks ouvertes : ${c.nb_tasks}`);
  if (briefContent) {
    const briefShort = briefContent.replace(/\s+/g, " ").slice(0, 400);
    lines.push(`\nBrief existant (extrait) : ${briefShort}`);
  }
  return lines.join("\n");
}

function fmtCompanySummary(
  c: CompanyOverviewRow,
  briefContent?: string | null,
) {
  const lines: string[] = [];
  lines.push(`**Entreprise :** ${c.name ?? `#${c.id}`}`);
  if (c.sector || c.size)
    lines.push(`Secteur / Taille : ${c.sector ?? "—"} / ${c.size ?? "—"}`);
  if (c.city) lines.push(`Ville : ${c.city}`);
  lines.push(
    `Contacts : ${c.contacts_total ?? 0} (${c.contacts_actifs ?? 0} actifs)`,
  );
  if (c.max_heat_tier)
    lines.push(
      `Heat tier max : ${c.max_heat_tier}${c.avg_heat_score ? ` (avg ${Math.round(c.avg_heat_score)})` : ""}`,
    );
  if (c.max_temperature)
    lines.push(`Température max (manuelle) : ${c.max_temperature}`);
  if (c.payment_state)
    lines.push(
      `Payment : ${c.payment_state}${c.mrr_monthly_eur ? ` — MRR ${c.mrr_monthly_eur}€/mois` : ""}`,
    );
  if (c.nb_open_deals)
    lines.push(
      `Pipeline ouvert : ${c.pipeline_open_eur ?? 0}€ (${c.nb_open_deals} deals)`,
    );
  if (c.has_overdue_tasks)
    lines.push(`⚠️ ${c.overdue_tasks_count} action(s) en retard`);
  if (c.age_days != null) lines.push(`Ancienneté : ${c.age_days}j`);
  if (briefContent) {
    const briefShort = briefContent.replace(/\s+/g, " ").slice(0, 400);
    lines.push(`\nBrief existant (extrait) : ${briefShort}`);
  }
  return lines.join("\n");
}

export function BriefAgentSheet({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityLabel,
  briefContent,
}: BriefAgentSheetProps) {
  const pageId = entityType === "contact" ? "contact" : "company";
  const suggestedPrompts =
    entityType === "contact"
      ? SUGGESTED_PROMPTS_CONTACT
      : SUGGESTED_PROMPTS_COMPANY;

  const resource =
    entityType === "contact" ? "contacts_summary" : "companies_overview";

  const { data: entityRow } = useGetOne(
    resource,
    { id: entityId },
    { enabled: open },
  );

  const entitySummary = (() => {
    if (!entityRow) return undefined;
    return entityType === "contact"
      ? fmtContactSummary(entityRow as ContactSummaryRow, briefContent)
      : fmtCompanySummary(entityRow as CompanyOverviewRow, briefContent);
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-5 py-4 border-b shrink-0">
          <SheetTitle className="text-base flex items-center gap-2">
            {entityType === "contact" ? (
              <User className="size-4 text-fabrik-violet" />
            ) : (
              <Building2 className="size-4 text-fabrik-violet" />
            )}
            Demander à l'agent · {entityLabel}
          </SheetTitle>
          <SheetDescription className="text-xs">
            L'agent connaît déjà cette fiche — pose ta question directement.
          </SheetDescription>
        </SheetHeader>

        {entitySummary && (
          <EntityCard
            summary={entitySummary}
            row={entityRow}
            entityType={entityType}
          />
        )}

        <div className="flex-1 min-h-0 flex flex-col">
          <EmbeddedAgentView
            pageId={pageId}
            pageLabel={entityLabel}
            scopeKey={String(entityId)}
            context={{
              entity_type: entityType,
              entity_id: String(entityId),
              entity_label: entityLabel,
              ...(entitySummary ? { entity_summary: entitySummary } : {}),
            }}
            suggestedPrompts={suggestedPrompts}
            className="flex-1"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EntityCard({
  summary,
  row,
  entityType,
}: {
  summary: string;
  row: ContactSummaryRow | CompanyOverviewRow | undefined;
  entityType: "contact" | "company";
}) {
  if (!row) return null;
  const updatedAt =
    entityType === "contact"
      ? (row as ContactSummaryRow).heat_updated_at
      : undefined;
  const heatTier =
    entityType === "contact"
      ? (row as ContactSummaryRow).heat_tier
      : (row as CompanyOverviewRow).max_heat_tier;
  return (
    <div className="px-5 py-3 border-b bg-muted/30 shrink-0">
      <div className="flex items-center gap-2 mb-1">
        <Flame className="size-3.5 text-fabrik-red" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Fiche pré-chargée
        </span>
        {heatTier && (
          <Badge variant="secondary" className="text-[10px] h-5">
            {heatTier}
          </Badge>
        )}
        {updatedAt && (
          <span className="text-[10.5px] text-muted-foreground ml-auto">
            scoré{" "}
            {formatDistanceToNowStrict(new Date(updatedAt), {
              addSuffix: true,
              locale: fr,
            })}
          </span>
        )}
      </div>
      <pre className="text-[12px] text-foreground/80 whitespace-pre-wrap font-sans leading-snug max-h-28 overflow-y-auto">
        {summary.length > 600 ? `${summary.slice(0, 600)}…` : summary}
      </pre>
    </div>
  );
}
